import React, { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';

const ProtectedVideoPlayer = ({ streamUrl, signatureParams }) => {
  const videoRef = useRef(null);
  const [hlsInstance, setHlsInstance] = useState(null);
  const [qualities, setQualities] = useState([]);
  const [currentQuality, setCurrentQuality] = useState(-1);

  useEffect(() => {
    let hls;
    const video = videoRef.current;
    
    // SAFETY CHECK: Is this an HLS playlist (.m3u8) or a legacy .mp4?
    const isHlsVideo = streamUrl && streamUrl.includes('.m3u8');

    if (isHlsVideo && Hls.isSupported()) {
      hls = new Hls({
        xhrSetup: function (xhr, url) {
          // SAFER CHECK: Look specifically for 'Policy=' to prevent double-appending signatures
          if (signatureParams && !url.includes('Policy=')) {
            const separator = url.includes('?') ? '&' : '?';
            xhr.open('GET', url + separator + signatureParams);
          }
        }
      });

      hls.loadSource(streamUrl);
      hls.attachMedia(video);

      hls.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
        // Extract available resolutions (1080p, 720p, 480p)
        const availableQualities = data.levels.map((level, index) => ({
          height: level.height,
          index: index
        }));
        // Sort highest resolution to the top
        setQualities(availableQualities.sort((a, b) => b.height - a.height));
      });

      setHlsInstance(hls);
      
    } else if (streamUrl) {
      // THE FALLBACK: If it is an old .mp4 file or the browser is Safari, play it natively
      video.src = streamUrl;
    }

    return () => {
      if (hls) {
        hls.destroy();
      }
    };
  }, [streamUrl, signatureParams]);

  const handleQualityChange = (e) => {
    const levelIndex = parseInt(e.target.value);
    
    if (hlsInstance) {
      // 1. Trigger the hard quality switch
      hlsInstance.currentLevel = levelIndex;
      setCurrentQuality(levelIndex);

      if (hlsInstance) {
        // The "YouTube" Method: Seamless transition
        // This finishes playing the current buffered segment before switching,
        // resulting in zero freezing or buffering delays.
        hlsInstance.nextLevel = levelIndex;
        setCurrentQuality(levelIndex);
      }
    }
  };

  return (
    <div className="relative bg-black rounded-lg overflow-hidden shadow-lg group w-full h-full flex items-center justify-center">
      {/* controlsList="nodownload" disables the native download button 
        on browsers when playing fallback .mp4 files 
      */}
      <video 
        ref={videoRef} 
        controls 
        controlsList="nodownload"
        className="w-full aspect-video outline-none" 
      />
      
      {/* Video Quality Selector (Only shows if HLS qualities are detected) */}
      {qualities.length > 0 && (
        <div className="absolute top-4 right-4 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
          <select 
            value={currentQuality} 
            onChange={handleQualityChange}
            className="bg-gray-900/80 text-white border border-gray-700 rounded-md px-3 py-1 text-sm outline-none backdrop-blur-sm cursor-pointer hover:bg-gray-800"
          >
            <option value={-1}>Auto</option>
            {qualities.map((q) => (
              <option key={q.index} value={q.index}>
                {q.height}p
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
};

export default ProtectedVideoPlayer;